import { Button } from "@sellar-io/components";
import { ReactElement, useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { Icon } from "../../../components";

export function SellarCreditCancelWarningDialog({
  active,
  onDismiss,
  onConfirmCancel,
  onEditOrder,
  type,
}: {
  active: boolean;
  onDismiss: () => void;
  onConfirmCancel: () => void;
  onEditOrder?: () => void;
  type: "supplier" | "buyer";
}): ReactElement {
  const [reasonSelected, setReasonSelected] = useState<string>();

  if (type === "buyer") {
    return (
      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
          title={"Are you sure you want to cancel this order?"}
          description={
            "Your order isn't set yet - it can be edited up until it is finalised"
          }
        />
        <DialogContent className="flex flex-col gap-2">
          <button
            type="button"
            className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 text-left"
            onClick={onConfirmCancel}
          >
            <figure className="flex h-12 w-12 flex-none items-center justify-center rounded bg-zinc-100">
              <Icon
                icon="NOTE_REMOVE_OUTLINE"
                size={1.2}
                className="text-zinc-800"
              />
            </figure>
            <div className="flex flex-col">
              <span className="font-semibold">
                Yes, I want to cancel my order
              </span>
              <p className="text-sm">
                The value of your cancelled order will be restored to your
                Sellar Credit balance
              </p>
            </div>
            <Icon className="shrink-0" icon={"CHEVRON_RIGHT"} />
          </button>
          {onEditOrder && (
            <button
              type="button"
              className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 text-left"
              onClick={onEditOrder}
            >
              <figure className="flex h-12 w-12 flex-none items-center justify-center rounded bg-zinc-100">
                <Icon icon="PENCIL" size={1.2} className="text-zinc-800" />
              </figure>
              <div className="flex flex-col">
                <span className="font-semibold">
                  No, I will edit the order instead
                </span>
                <p className="text-sm">
                  Whilst your order status is requested or accepted, you can
                  freely edit your order
                </p>
              </div>
              <Icon className="shrink-0" icon={"CHEVRON_RIGHT"} />
            </button>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Issue with this order?"}
        description={"Please select your intention below"}
      />
      <DialogContent className="flex flex-col gap-2">
        <InputLabel>*Reason</InputLabel>
        <InputContainer>
          <InputSelect
            value={reasonSelected}
            onChange={(e) => setReasonSelected(e.target.value)}
          >
            <InputOption>-- Select Reason --</InputOption>
            <InputOption value={"cancel"}>
              I don't intend on fulfilling the order for this customer
            </InputOption>
            <InputOption value={"edit"}>
              I need to make order edits and recreate the order on behalf of the
              buyer
            </InputOption>
          </InputSelect>
        </InputContainer>
        <div className="flex items-center gap-4 rounded-lg border border-gray-200 p-4">
          <figure className="flex h-12 w-12 flex-none items-center justify-center rounded bg-zinc-100">
            <Icon icon="INFORMATION" size={1.2} className="text-blue-600" />
          </figure>
          <div className="flex flex-col">
            <span className="font-semibold">
              Need to make changes to the order?
            </span>
            <p className="text-sm">
              Your customer can edit the order directly on their side. If that's
              not possible, you can recreate the order with the required changes
              and select Sellar Credit to keep their payment terms the same.
            </p>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className={"flex flex-col gap-2 md:flex-row"}>
        <>
          {reasonSelected && (
            <Button
              color={"tertiary"}
              className={"w-full md:w-auto"}
              onClick={onConfirmCancel}
            >
              I still want to cancel this order
            </Button>
          )}
          <Button className={"w-full md:w-auto"} onClick={onDismiss}>
            Go back
          </Button>
        </>
      </DialogCTA>
    </Dialog>
  );
}
